import { supabase } from './supabase'
import { publicUrl } from './publicUrl'

/**
 * crypto.randomUUID() needs Safari 15.4 and the iOS build targets 15.0, so
 * fall back to getRandomValues — an invite token has to stay unguessable.
 */
export function randomToken() {
  if (typeof crypto !== 'undefined') {
    if (crypto.randomUUID) return crypto.randomUUID()
    if (crypto.getRandomValues) {
      const a = new Uint8Array(16)
      crypto.getRandomValues(a)
      return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('')
    }
  }
  return `${Date.now()}${Math.random().toString(36).slice(2)}`
}

/**
 * Add someone to a care team and email them the link.
 *
 * The row is the source of truth: if the email fails to send the invite is
 * still valid and the returned link can be shared by hand.
 *
 * Returns { ok, inviteUrl, emailed, error }.
 */
export async function inviteMember({
  recipientId, recipientName, name, email, role,
  relationship = null, inviterName = 'A coordinator',
  scheduleJson = null, sendEmail = true, token = randomToken(),
}) {
  const row = {
    care_recipient_id: recipientId,
    invited_name: String(name || '').trim(),
    invited_email: String(email || '').trim().toLowerCase(),
    role,
    relationship,
    invite_token: token,
    is_active: true,
  }
  if (scheduleJson) row.schedule_json = scheduleJson

  let { error } = await supabase.from('care_team_members').insert(row)

  // schedule_json arrives in its own migration. If the app is deployed first,
  // save the membership without it rather than losing the invite entirely.
  if (error && isMissingColumn(error, 'schedule_json')) {
    const { schedule_json, ...withoutSchedule } = row
    ;({ error } = await supabase.from('care_team_members').insert(withoutSchedule))
  }
  if (error) return { ok: false, error }

  if (!sendEmail) return { ok: true, inviteUrl: publicUrl(`/invite?token=${token}`), emailed: false }

  const inviteUrl = publicUrl(`/invite?token=${token}`)
  let emailed = true
  try {
    await supabase.functions.invoke('send-invite', {
      body: {
        to: String(email).trim().toLowerCase(),
        invite_url: inviteUrl,
        recipient_name: recipientName,
        inviter_name: inviterName,
        role,
      },
    })
  } catch {
    emailed = false
  }
  return { ok: true, inviteUrl, emailed }
}

export const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim())

/** Postgres 42703 / PostgREST PGRST204 both mean "no such column". */
function isMissingColumn(error, column) {
  if (!error) return false
  const code = error.code || ''
  const msg = `${error.message || ''}`.toLowerCase()
  return (code === '42703' || code === 'PGRST204') && msg.includes(column)
}

/** "09:00" + "13:00" → "9:00 AM – 1:00 PM". Returns null unless both are set. */
export function formatShiftHours(start, end) {
  const fmt = (t) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''))
    if (!m) return null
    let h = parseInt(m[1], 10)
    const suffix = h >= 12 ? 'PM' : 'AM'
    h = h % 12 || 12
    return `${h}:${m[2]} ${suffix}`
  }
  const a = fmt(start)
  const b = fmt(end)
  return a && b ? `${a} – ${b}` : null
}
